<template>
  <div class="lens">
    <!-- Card step: avatar + story beat (left) · card image (right) -->
    <div v-if="!isSummary && active" class="lens-step">
      <div class="lens-cell lens-left">
        <div v-if="avatar" class="lens-av"><img :src="avatar" alt="" /></div>
        <p class="lens-meaning">{{ active.meaning }}</p>
      </div>
      <div class="lens-cell lens-right">
        <div class="lens-card" @click="$emit('card-detail', sel)">
          <img v-if="active.image" :src="active.image" :alt="active.name" :class="{ reversed: active.reversed }" />
          <div v-else class="lens-card-ph"></div>
        </div>
        <div class="lens-caption">
          <span class="lens-pos">{{ active.position }}</span>
          <h4 class="lens-name">{{ active.name }}<em v-if="active.reversed"> · reversed</em></h4>
        </div>
      </div>
    </div>

    <!-- Summary step: avatar + moral (left) · illustration (right) -->
    <div v-else-if="isSummary" class="lens-step">
      <div class="lens-cell lens-left">
        <div v-if="avatar" class="lens-av"><img :src="avatar" alt="" /></div>
        <p class="lens-meaning">{{ moral }}</p>
      </div>
      <div class="lens-cell lens-right">
        <p v-if="caption" class="lens-imgcaption">{{ caption }}</p>
        <div v-if="illustration" class="lens-illus" @click="$emit('zoom', illustration)">
          <img :src="illustration" alt="Reading illustration" />
        </div>
        <slot name="credit" />
      </div>
    </div>

    <!-- Bottom bar: compact ‹ step › nav (centered under the paragraph) · actions (under the image) -->
    <div class="lens-bar">
      <div class="lens-navcell">
        <nav v-if="stepCount > 1" class="lens-nav">
          <button class="lens-arrow" :disabled="sel === 0" @click="prev" aria-label="Previous">‹</button>
          <span class="lens-step-label">{{ currentLabel }}</span>
          <button class="lens-arrow" :disabled="sel >= stepCount - 1" @click="next" aria-label="Next">›</button>
        </nav>
      </div>
      <div class="lens-actcell"><slot name="actions" /></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  cards: { type: Array, default: () => [] },     // { position, name, image, reversed, meaning }
  moral: { type: String, default: '' },          // the moral of the story (revealed last)
  caption: { type: String, default: '' },         // the image caption (share_takeaway), above the illustration
  illustration: { type: String, default: '' },
  avatar: { type: String, default: '' },         // seeker avatar — its own cell, left of the image
})
defineEmits(['zoom', 'card-detail'])

const sel = ref(0)
const hasSummary = computed(() => props.cards.length >= 1 && (!!props.moral || !!props.illustration))
const stepCount = computed(() => props.cards.length + (hasSummary.value ? 1 : 0))
const isSummary = computed(() => hasSummary.value && sel.value >= props.cards.length)
const active = computed(() => props.cards[Math.min(sel.value, props.cards.length - 1)] || null)
const currentLabel = computed(() => isSummary.value ? '✦ Summary' : (active.value?.position || ''))

function prev() { sel.value = Math.max(0, sel.value - 1) }
function next() { sel.value = Math.min(stepCount.value - 1, sel.value + 1) }
</script>

<style scoped>
.lens { display: flex; flex-direction: column; gap: 18px; }

/* Two cells: avatar + text (left) · image (right). Anchored to the top so the
   avatar (and the card) stay put while the paragraph grows downward. Fixed
   min-height so the bottom bar (nav + buttons) doesn't jitter as card/illustration
   heights and paragraph lengths change between steps. */
.lens-step { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; min-height: 290px; }
.lens-cell { display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; }

/* Left cell — large round seeker avatar, then the story paragraph */
.lens-av { width: 104px; height: 104px; border-radius: 50%; overflow: hidden;
  border: 2px solid #FFDFA3; box-shadow: 0 4px 18px rgba(0,0,0,0.5); background: #1c1030; }
.lens-av img { width: 100%; height: 100%; object-fit: cover; display: block; }
.lens-meaning { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px;
  color: rgba(255,255,255,0.85); line-height: 1.6; margin: 0; }

/* Right cell — the image, with its title + position beneath */
.lens-card { width: 150px; border-radius: 10px; overflow: hidden; cursor: pointer;
  box-shadow: 0 6px 22px rgba(0,0,0,0.45); }
.lens-card img { width: 100%; display: block; transition: transform 0.3s ease; }
.lens-card img.reversed { transform: rotate(180deg); }
.lens-card-ph { width: 150px; height: 228px; background: rgba(93,59,140,0.3); }
.lens-illus { width: min(280px, 100%); border-radius: 12px; overflow: hidden; cursor: pointer;
  box-shadow: 0 6px 22px rgba(0,0,0,0.45); }
.lens-illus img { width: 100%; display: block; }
/* The quote (share_takeaway) centered just above the illustration. */
.lens-imgcaption { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 14px;
  line-height: 1.4; text-align: center; color: rgba(255,255,255,0.92); margin: 0; max-width: 280px; }

.lens-caption { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.lens-pos { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.08em;
  text-transform: uppercase; color: rgba(195,169,255,0.6); }
.lens-name { font-family: 'Cinzel', serif; font-size: 18px; color: #FFDFA3; margin: 0; line-height: 1.25; }
.lens-name em { font-style: italic; font-size: 13px; color: rgba(195,169,255,0.7); }

/* Bottom bar — nav under the words (centered), actions under the image (right) */
.lens-bar { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: center;
  padding-top: 16px; border-top: 1px solid rgba(93,59,140,0.22); }
.lens-navcell { display: flex; justify-content: center; }
.lens-actcell { display: flex; justify-content: flex-end; align-items: center; }

/* Compact ‹ step › filmstrip */
.lens-nav { display: inline-flex; align-items: center; gap: 10px; }
.lens-arrow { width: 26px; height: 26px; flex: none; border-radius: 50%; cursor: pointer;
  font-family: serif; font-size: 16px; line-height: 1; color: #C3A9FF;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(93,59,140,0.5); transition: all 0.18s ease; }
.lens-arrow:hover:not(:disabled) { border-color: rgba(255,223,163,0.55); color: #f0d9a3; }
.lens-arrow:disabled { opacity: 0.3; cursor: default; }
.lens-step-label { min-width: 92px; text-align: center; font-family: 'Cinzel', serif; font-size: 11px;
  letter-spacing: 0.06em; color: #FFDFA3; background: rgba(240,217,163,0.16);
  border: 1px solid rgba(240,217,163,0.5); border-radius: 999px; padding: 6px 14px; }

@media (max-width: 560px) {
  .lens-step, .lens-bar { grid-template-columns: 1fr; }
  .lens-actcell { justify-content: center; }
}
</style>
